import React from 'react';
import { RequireFeature } from 'components/entitlements';
import type { FeatureKey } from 'modules/entitlements';
import type { Permission } from 'modules/auth/permissions';
import { RequirePermission } from './RequirePermission';

interface GateProps {
  /** Plan entitlement required (optional). */
  feature?: FeatureKey;
  /** Role permission required — single key or any-of list (optional). */
  permission?: Permission | Permission[];
  /** 'route': a locked feature shows the upgrade prompt, denied permission redirects. 'affordance': renders nothing. */
  mode?: 'route' | 'affordance';
  children: React.ReactNode;
}

/** Evaluation order: feature (plan) FIRST, then permission (RBAC). Both are SOFT — the backend enforces. */
export function Gate({ feature, permission, mode = 'route', children }: GateProps) {
  let content = <>{children}</>;

  // Inner: permission (role) gate.
  if (permission !== undefined) {
    content = (
      <RequirePermission permission={permission} mode={mode}>
        {content}
      </RequirePermission>
    );
  }

  // Outer: feature (plan) gate — evaluated first so a plan lock wins over a
  // permission redirect.
  if (feature !== undefined) {
    content = (
      <RequireFeature feature={feature} silent={mode === 'affordance'}>
        {content}
      </RequireFeature>
    );
  }

  return content;
}
